/**
 * src/services/uploadService.ts
 * =============================================================================
 * Cliente del flujo "crear share anónimo" desde la landing pública.
 *
 * Flow:
 *   1. POST a `/submit-share` en api.ploter.me (Worker).
 *   2. La ruta devuelve { uploadUrl, objectKey, waitlistId }.
 *   3. PUT directo a R2 con la presigned URL (no pasa por el Worker).
 *   4. Resolución con { waitlistId, objectKey } cuando R2 confirma 200.
 *
 * Usamos XMLHttpRequest en vez de fetch para el PUT a R2 porque fetch
 * NO expone el progreso de subida (es una limitación histórica del
 * estándar). Para mostrar "Subiendo… 47%" en el botón hace falta XHR.
 *
 * Manejo de errores:
 *  - El Worker devuelve `{error:'code', details?}` con status 4xx → lo
 *    convertimos a `SubmitShareError` con `code` semántico.
 *  - R2 devuelve no-2xx en el PUT → upload_failed (URL expiró o
 *    Content-Type mismatch).
 *  - network_error si fetch/XHR no llegaron a respuesta.
 * =============================================================================
 */

import { recordarQueSoyElRemitente } from '@/services/shareNotifyService'
import { api, ApiError } from '@/lib/api'

export interface SubmitShareArgs {
  file: File
  /**
   * Email del destinatario. OPCIONAL — el flujo "drop → editor directo"
   * sube primero y permite añadir destinatarios después desde el visor.
   */
  recipientEmail?: string
  /** Si el usuario está logueado podemos pasar su email; en la landing
   *  anónima estilo WeTransfer NO se pide. */
  senderEmail?: string
  message?: string
  locale: string
  onProgress?: (fraction: number) => void
}

export interface SubmitShareResult {
  waitlistId: string
  objectKey: string
}

export class SubmitShareError extends Error {
  /** Código semántico — el caller decide cómo localizarlo. */
  code: string
  /**
   * Detalles del servidor (p. ej. `bytesLimit` con la cuota llena). Se
   * conservan para que el modal de límite diga la cifra real del plan en
   * vez de suponer siempre "300 MB".
   */
  details?: Record<string, unknown>
  constructor(code: string, message?: string, details?: Record<string, unknown>) {
    super(message ?? code)
    this.code = code
    this.details = details
  }
}

/** Envoltorio: cualquier ApiError se rebrandea como SubmitShareError (con sus detalles). */
function rebrandError(e: unknown): SubmitShareError {
  if (e instanceof ApiError) return new SubmitShareError(e.code, e.message, e.details)
  if (e instanceof Error) return new SubmitShareError('request_failed', e.message)
  return new SubmitShareError('request_failed')
}

export async function submitShare(args: SubmitShareArgs): Promise<SubmitShareResult> {
  const { file, senderEmail, recipientEmail, message, locale, onProgress } = args

  // 1) Pedir presigned URL + crear fila waitlist. Ruta anónima (sin auth).
  // Secreto que demuestra que ESTE navegador subió el archivo. Va al servidor
  // como huella y se queda aquí en claro. Es lo único que después distingue al
  // remitente del destinatario: los dos abren la misma página con el mismo
  // enlace. Ver `migrations/0011_remitente_verificable.sql`.
  const senderKey = crypto.randomUUID()

  let response: { uploadUrl: string; objectKey: string; waitlistId: string }
  try {
    response = await api<{ uploadUrl: string; objectKey: string; waitlistId: string }>(
      '/submit-share',
      {
        method: 'POST',
        credentials: false,
        body: {
          filename: file.name,
          size: file.size,
          contentType: file.type,
          senderEmail: senderEmail ?? null,
          recipientEmail: recipientEmail ?? null,
          message,
          locale,
          senderKey,
        },
      },
    )
  } catch (e) {
    throw rebrandError(e)
  }

  if (!response?.uploadUrl || !response.objectKey || !response.waitlistId) {
    throw new SubmitShareError('invalid_response')
  }

  // 2) PUT directo a R2. XHR para tener progreso.
  await putWithProgress(response.uploadUrl, file, onProgress)

  // El Worker comprueba ahora los bytes almacenados, no el MIME declarado
  // por el navegador. Hasta que confirme, el share no se considera listo.
  try {
    await api('/confirm-share-upload', {
      method: 'POST',
      credentials: false,
      body: { token: response.waitlistId },
    })
  } catch (e) {
    throw rebrandError(e)
  }

  // Se recuerda DESPUÉS de que R2 confirme, no antes: si la subida falla no
  // queda un secreto suelto para un envío que no existe.
  recordarQueSoyElRemitente(response.waitlistId, senderKey)

  return { waitlistId: response.waitlistId, objectKey: response.objectKey }
}

function putWithProgress(
  url: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    // El Content-Type DEBE coincidir con el que se firmó en el backend
    // (application/pdf). Si no, R2 rechaza con 403/400.
    xhr.setRequestHeader('Content-Type', file.type)

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total)
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(1)
        resolve()
      } else {
        reject(new SubmitShareError('upload_failed', `R2 ${xhr.status}`))
      }
    }
    xhr.onerror = () => reject(new SubmitShareError('network_error'))
    xhr.onabort = () => reject(new SubmitShareError('aborted'))

    xhr.send(file)
  })
}

// ---------------------------------------------------------------------------
// Subir una versión NUEVA a un share existente (drop-in v2/v3/...).
// El servidor encadena la nueva fila al root via parent_waitlist_id y le
// asigna version_number = max(siblings) + 1.
// ---------------------------------------------------------------------------

export interface UploadShareVersionArgs {
  /** Token actual del share — el Worker resuelve la raíz del chain. */
  token: string
  file: File
  /**
   * Clave secreta del remitente (localStorage, por rootToken). Desde el
   * 2026-09-04 el Worker EXIGE que coincida con el hash de la raíz para subir
   * versiones nuevas: el destinatario ve y anota, pero no sube — y sobre todo
   * no renueva el TTL de la cadena indefinidamente. En cadenas antiguas sin
   * hash el Worker no la pide, así que null es válido ahí.
   */
  senderKey?: string | null
  onProgress?: (fraction: number) => void
}

export interface UploadShareVersionResult {
  waitlistId: string
  versionNumber: number
  rootToken: string
  objectKey: string
}

export async function uploadShareVersion(
  args: UploadShareVersionArgs,
): Promise<UploadShareVersionResult> {
  const { token, file, senderKey, onProgress } = args
  let r: {
    uploadUrl: string
    waitlistId: string
    versionNumber: number
    rootToken: string
    objectKey: string
  }
  try {
    r = await api('/upload-share-version', {
      method: 'POST',
      credentials: false,
      body: {
        token,
        filename: file.name,
        size: file.size,
        contentType: file.type,
        ...(senderKey ? { senderKey } : {}),
      },
    })
  } catch (e) {
    throw rebrandError(e)
  }
  if (!r?.uploadUrl || !r.waitlistId || !r.rootToken || !r.objectKey) {
    throw new SubmitShareError('invalid_response')
  }
  await putWithProgress(r.uploadUrl, file, onProgress)
  try {
    await api('/confirm-share-upload', {
      method: 'POST',
      credentials: false,
      body: { token: r.waitlistId },
    })
  } catch (e) {
    throw rebrandError(e)
  }
  return {
    waitlistId: r.waitlistId,
    versionNumber: r.versionNumber ?? 1,
    rootToken: r.rootToken,
    objectKey: r.objectKey,
  }
}
